import GoogleMapReact from 'google-map-react';
import { ComponentType, ReactNode } from 'react';
import { Layout, Layouts } from 'react-grid-layout';

import { PRIORITY_NAMES } from '@portals/utils';

import { FieldType } from './autoformik';
import {
  CommonConfigurationType,
  CommonFeatureFlagsType,
  CommonPortalCapabilities,
} from './configuration';
import { AccessLevelEnum, UserPermissionsCallback } from './permissions';
import { StateType } from './redux';

export type ComponentRendererType<TProps extends object = Record<string, any>> =
  ComponentType<TProps>;

export interface CanAccessRouteFnParameters<
  TCommonConfigurationType extends CommonConfigurationType
> {
  canView: UserPermissionsCallback;
  canEdit: UserPermissionsCallback;
  configuration: TCommonConfigurationType | undefined;
  isAdmin: boolean;
  hasSupportSeat: boolean;
}

export type CanAccessRouteFn<
  TCommonConfigurationType extends CommonConfigurationType
> = (params: CanAccessRouteFnParameters<TCommonConfigurationType>) => boolean;

export type TabVisibleFn<
  ExtraParams extends object,
  TFeatureFlags extends CommonFeatureFlagsType = CommonFeatureFlagsType,
  TPortalCapabilities extends CommonPortalCapabilities = CommonPortalCapabilities
> = (
  params: {
    featureFlags: TFeatureFlags;
    portalCapabilities: TPortalCapabilities;
    canView: UserPermissionsCallback;
    canEdit: UserPermissionsCallback;
    isAdmin: boolean;
  } & ExtraParams
) => boolean;

export interface RoutesMap<
  TCommonConfigurationType extends CommonConfigurationType
> {
  dashboard: CategoryRouteItem<TCommonConfigurationType>[];
  auth?: RoutesType<TCommonConfigurationType>;
  externalStore?: RoutesType<TCommonConfigurationType>;
  unsubscribe?: RoutesType<TCommonConfigurationType>;
  routeModals?: EntityRouteType<TCommonConfigurationType>[];
  routePanels?: EntityRouteType<TCommonConfigurationType>[];
}

export interface CategoryRouteItem<
  TCommonConfigurationType extends CommonConfigurationType
> {
  id: string;
  headerId: string;
  childRoutes: RoutesType<TCommonConfigurationType>;
  canAccessRoute?: CanAccessRouteFn<TCommonConfigurationType>;
}

export type RouteType<
  TCommonConfigurationType extends CommonConfigurationType
> = {
  headerId?: string;
  href?: string;
  path:
    | ((params: {
        configuration: TCommonConfigurationType | undefined;
        isAdmin: boolean;
      }) => Array<string>)
    | Array<string>
    | string;
  id?: string;
  component?: ComponentRendererType;
  icon?: ComponentRendererType;
  badgeComponent?: ComponentRendererType;
  indicatorComponent?: ComponentRendererType;
  badgeColor?: string;
  badgeText?: string;
  isVisibleInSidebar?: (params: {
    configuration: TCommonConfigurationType | undefined;
  }) => boolean;
  canAccessRoute?: CanAccessRouteFn<TCommonConfigurationType>;
  children?: Array<RouteType<TCommonConfigurationType>>;
  open?: boolean;
  containsHome?: boolean;
  hasBorder?: boolean;
};

export interface EntityRouteType<
  TCommonConfigurationType extends CommonConfigurationType
> extends RouteType<TCommonConfigurationType> {
  path: string | Array<string>;
  backgroundPath: string;
  generatePath: (params: Array<string>) => string;
}

export enum TenantType {
  Organization = 'organization',
  Partner = 'partner',
}

export type CustomColorName = `custom_color_${string}`;

export interface CustomColor {
  colorHex: string;
}

export interface DesignType {
  sign_in_page?: {
    logo: string | null;
    login_image: string | null;
    tagline: string | null;
    powered_by_xyte_label: boolean | null;
    tagline_color: string | null;
    background_color: string | null;
    background_image: string | null;
    background_mode: 'image' | 'color';
    primary_color: string | null;
    text_color: string | null;
  };
  cec_auth_page?: {
    logo?: string;
    tagline?: string;
  };
  cec_connect_page?: {
    logo?: string;
    about?: string;
  };
  cec_device_claiming?: {
    logo?: string;
    banner?: string;
  };
  cec_devices?: {
    logo?: string;
  };
  cec_tickets?: {
    logo?: string;
  };
  navigation_menu?: {
    logo: string | null;
  };
  custom_colors?: string[];
  store?: {
    logo?: string | null;
    banner?: string | null;
    tagline?: string | null;
    with_banner?: boolean;
  };
}

export type RoutesType<
  TCommonConfigurationType extends CommonConfigurationType
> = Array<RouteType<TCommonConfigurationType>>;
export type ModalsType = Record<string, any>;

export type SidebarLayoutCallback<
  TCommonConfigurationType extends CommonConfigurationType
> = (params: {
  configuration: TCommonConfigurationType | undefined;
}) => ReactNode;

export type ExtraLayoutType<
  TCommonConfigurationType extends CommonConfigurationType
> = {
  dashboardLayout?: any;
  externalStoreLayout?: any;
  authLayout?: any;
  globalLayout?: () => JSX.Element;
  navbarLayout?: () => JSX.Element;
  sidebarLayout?: SidebarLayoutCallback<TCommonConfigurationType>;
  dashboardDataLayout?: ComponentType<{
    children: ReactNode;
  }>;
  options?: {
    isMobile?: boolean;
  };
};

export interface AppProps<
  TCommonConfigurationType extends CommonConfigurationType
> {
  readonly routes: RoutesMap<TCommonConfigurationType>;
  readonly modals: ModalsType;
  readonly tenantType: TenantType;
  readonly extraLayout?: ExtraLayoutType<TCommonConfigurationType>;
}

export type TelemetryType = {
  id: string;
  created_at: string;
  timestamp: string;
  device_timestamp: string;
  common: JSON;
};

export type IncidentPriorityLevel = 1 | 2 | 3 | 4 | 5;

export type OrganizationIncidentType = {
  device_id: string;
  status: string;
  id: string;
  priority: IncidentPriorityLevel;
  title: string;
  description: string;
  issue: string;
  created_at: string;
  closed_at: string;
  ttc: string;
  space_id: string;
  urls: Record<string, string>;
  updated_at: string;
  access_level: AccessLevelEnum;
  device_model: string;
  device_name: string;
  org_display_name: string;
  partner_display_name: string;
  space_name: string;
};

export type AccessType = Record<string, Record<UuidType, AccessLevelType>>;

export interface SpaceStateIncidentsType {
  low: number;
  high: number;
  critical: number;
  moderate: number;
  planning: number;
  total: number;
}

export type DeviceStatusType = 'online' | 'offline' | 'unavailable' | 'error';

// Partner-only, should be moved to @portals/api/partners when devices are moved to RQ from
// redux in Partners portal
export type DeviceType = {
  parent: null | string;
  id: string;
  name: string;
  space_id: number;
  state: {
    status?: DeviceStatusType;
  };
  snoozed_until?: string;
  config_version: number;
  applied_config_version: number | null;
  status: DeviceStatusType;
  type: string;
  model_settings: {
    icon?: DeviceModelIconName;
    commands: Array<SupportedCommandType>;
    visibility: Record<string, unknown>;
  };
  partner: {
    sn: null | any;
    mac: null | any;
    vendor: string;
    model: string;
    type_id: string;
    cloud_id?: string;
    sub_model?: string;
    logo: string | null;
  };
  details: null | any;
  firmware: string;
  config: {
    other: {
      sample: number;
    };
    network: {
      ip: string;
      port: number;
    };
    version: number;
    last_updated: string;
  };
  organization?: {
    id: string;
    name: string;
    logo_url: string | null;
  };
  config_schema: null | any;
  is_config_schema_readonly: boolean;
  driver: null | any;
  incidents: Array<number>;
  last_seen: string;
  access_key: string;
  custom_info: Record<string, any>;
  custom_info_version: number;
  access_level: AccessLevelEnum;
  claimed?: boolean;
  communication_protocol: string;
  device_model_id?: UuidType;
  device_model_active?: boolean;
  model?: string;
  created_at?: string;
  claimed_at?: string;
  partner_note?: string;
  note?: string;
  sn?: string;
  mac?: string;
  cloud_id?: string;
  connection_info: {
    access_key?: string;
    hub_url?: string;
    hub_url_static_cert?: string;
    mqtt_hub_url?: string;
  };
  registration_failed_at: string | null;
  awaiting_replacement: boolean;
  space_tree_path_name: string;
  migrated_to_aws: boolean;

  tabs: {
    commands: boolean;
    configuration: boolean;
    dumps: boolean;
    files: boolean;
    licenses: boolean;
    state: boolean;
    telemetries: boolean;
  };
  test: boolean;
  image_url: string | null;
};

export type IntegrationExtraType = {
  item_fields: Array<{ name: string; label: string; choices: Array<string> }>;
  required_fields: Array<{
    name: string;
    label: string;
    choices: Array<string>;
  }>;
  devices?: Array<any>;
  import?: Record<string, any>;
} & Record<string, any>;

interface ZoomRoomType {
  id: string;
  location_id: string;
  name: string;
  room_id: string;
  status: string;
}

export interface IntegrationsExtraZoomType {
  name: 'zoom';
  state: string;
  updated_at: string;
  import: {
    devices: number;
    spaces: number;
    state: string;
  };
  rooms: Array<ZoomRoomType>;
  unclaimed: Array<ZoomRoomType>;
}

export interface IntegrationExtraBrightSignType {
  error: string;
  networks: string[];
  devices: Array<{
    id: number;
    name: string;
    serial: string;
  }>;
}

export enum IntegrationState {
  Active = 'active',
  Disabled = 'disabled',
  Error = 'error',
  Unauthenticated = 'unauthenticated',
}

export interface IntegrationType<ExtraType = IntegrationExtraType> {
  name: string;
  state: IntegrationState;
  updated_at: string;
  created_at: string;
  params: Record<string, any>;
  extra: ExtraType;
  error: null | any;
  devices?: number;
  id: string;
  space_id?: number;
  setup_link?: {
    code: string;
    expires_at: string;
    url: string;
  };
}

export type NotificationType = {
  id: number;
  title: string;
  message: string;
  link?: string;
};

export type SystemIssuesType = {
  id: number;
  title: string;
  description: string;
  link?: string;
  entity: { type: string; id: string };
};

export interface UserPermissions {
  models: AccessLevelEnum;
  customers: AccessLevelEnum;
  finance: AccessLevelEnum;
  store_management: AccessLevelEnum;
  fulfillment: AccessLevelEnum;
}

export type UserPermissionNames = keyof UserPermissions;

export enum RefereeType {
  Organization = 'organization',
}

export interface RuleError {
  id: string;
  name: string;
  description: string;
  created_at: string;
}

export interface RuleType {
  id: string;
  name: string;
  active: boolean;
  device_model: string;
  description: string;
  priority: keyof typeof PRIORITY_NAMES;
  created_at: string;
  definition: any;
  errors: RuleError[];
}

export type DashboardLayoutListItem<
  TID = string,
  TExtraParams = Record<string, any>
> = Layout & {
  id: TID;
  name: string;
  extraParams?: TExtraParams;
};

export type DashboardLayout<
  TID = string,
  TExtraParams = Record<string, any>
> = {
  list: DashboardLayoutListItem<TID, TExtraParams>[];
  layouts: Layouts;
};

export type SupportedCommandFieldType = FieldType<{
  value: string;
  label: string;
  order: number;
  telemetry_value?: string;
  required?: boolean;
}>;

export interface SupportedCommandProductType {
  id: string;
  name: string;
  image_url: string;
}

export type SupportedCommandType = {
  id: UuidType;
  name: string;
  device_id: string;
  status: string;
  friendly_name: string;
  description: string;
  with_file: null | boolean;
  file_type?: string;
  custom_fields: null | Array<SupportedCommandFieldType>;
  active: boolean;
  timeout_in_seconds: number;
  allow_duplicate_commands: boolean;
  created_at: string;
  updated_at: string;
  is_visible_in_panel: boolean;
  required?: boolean;
  premium: boolean;
  device_model_name?: string;
  command_features?: string[];
  product_ids?: string[];
  has_license: boolean;
  products: SupportedCommandProductType[];
  confirmation_message: string | null;
};

export enum AuthMethodType {
  CloudId = 'cloud_id',
  MacSN = 'mac_sn',
  PubKey = 'pub_key',
  x509 = 'x509',
}

export enum CommunicationProtocolType {
  HTTPS = 'https',
  MQTT = 'mqtt',
  VPRO = 'vPro',
}

export type TicketStatusType = 'open' | 'replied' | 'review' | 'resolved';

export type TicketType = {
  id: string;
  status: TicketStatusType;
  title: string;
  description: string;
  device_id: string;
  device_name?: string;
  partner: string;
  partner_logo_url: string | null;
  organization_display_name: string;
  organization_logo_url: string | null;
  org_seen: boolean;
  partner_seen: boolean;
  created_at: string;
  updated_at: string;
  messages_count: number;
  device?: {
    name: string;
    model: string;
    sn: null | any;
    model_icon?: string;
  };
  chat?: ChatMessageType[];
  space?: {
    name: string;
    path: string;
  };
  org_id?: string;
};

export interface ChatMessageType {
  created_at: string;
  id: number;
  owner: string;
  owner_type: TenantType | 'system';
  owner_logo?: string | null;
  system: boolean;
  text: string;
  user: string | null;
}

export interface OrganizationFileResponseType {
  id: string;
  name: string;
  url: string;
  desc: string | null;
  type: string | null;
  version: string | null;
  device_model: string | null;
  device_model_name: string | null;
  space: number | null;
  space_name: string | null;
  public_notes: string | null;
  partner: boolean;
  created_at: string;
}

export interface PartnerFileResponseType {
  id: string;
  name: string;
  url: string;
  desc: string;
  type: string;
  file_type: string;
  version: string;
  created_at: string;
  device_model_id: string;
  device_model_name: string;
  checksum: string;
  signature: string;
  notes: string;
  public_notes: string;
  published: boolean;
}

export enum LicenseState {
  Available = 'available',
  Inuse = 'inuse',
  Renew = 'renew',
  Error = 'error',
  Remove = 'remove',
  Removed = 'removed',
  Disable = 'disable',
  Disabled = 'disabled',
}

export type RelatedProductToCommandType = {
  category: string;
  licenses: LicenseType[];
  description: string;
  device_model_id: string;
  id: string;
  created_at: string;
  image_url: string | null;
  name: string;
  partner_display_name: string;
  partner_logo_url: string | null;
  sku: string | null;
  subtitle: string | null;
  transferable: boolean;
};

export type LicenseType = {
  id: string;
  state: LicenseState;
  family: null | any;
  amount: number;
  transferable: boolean;
  expires_at: null | string;
  activated_at: null | string;
  created_at: string;
  duration: null | any;
  partner_display_name: string;
  product_name: string;
  product_id: string;
  product_image: string | null;
  product_subtitle: string | null;
  product_category: string | null;
  product_payment_interval: PeriodEnum;
  device_error_message: string | null;
  device_id: string;
  device_model_id?: string;
  model: string;
  org_license: boolean;
  assigned?: boolean;
  claimed?: boolean;
  notes?: string;
  data?: string;
  signature?: string;
  access_level: AccessLevelEnum;
  params?: {
    sn: string;
  };
  organization: {
    id: string;
    name: string;
  };
};

// Define a utility type for the keys
export type LocationGenericKeys =
  | 'FeatureFlags'
  | 'TabContentComponentProps'
  | 'PathParams'
  | 'VisibleFnExtraParams';

// Define a mapped type for the generics with constraints
export type LocationGeneric<
  T extends Partial<Record<LocationGenericKeys, any>>
> = {
  FeatureFlags: T['FeatureFlags'] extends CommonFeatureFlagsType
    ? T['FeatureFlags']
    : CommonFeatureFlagsType;
  TabContentComponentProps: T['TabContentComponentProps'] extends object
    ? T['TabContentComponentProps']
    : Record<string, unknown>;
  PathParams: T['PathParams'] extends object
    ? T['PathParams']
    : Record<string, string>;
  VisibleFnExtraParams: T['VisibleFnExtraParams'] extends object
    ? T['VisibleFnExtraParams']
    : Record<string, unknown>;
};

export type TabType<
  TFeatureFlags extends CommonFeatureFlagsType = CommonFeatureFlagsType,
  TPortalCapabilities extends CommonPortalCapabilities = CommonPortalCapabilities,
  ComponentProps extends object = Record<string, unknown>,
  VisibleFnExtraParams extends object = Record<string, unknown>,
  TDevice extends object = Record<string, unknown>
> = {
  title: string;
  id: string;
  name?: string;
  Component?: ComponentRendererType<ComponentProps>;
  props?: Record<string, any>;
  tabs?: TabType<
    TFeatureFlags,
    TPortalCapabilities,
    ComponentProps,
    VisibleFnExtraParams,
    TDevice
  >[];
  visible?: TabVisibleFn<
    VisibleFnExtraParams,
    TFeatureFlags,
    TPortalCapabilities
  >;
  disabled?: boolean;
  badge?: ReactNode;
  Counter?: ComponentRendererType<ComponentProps>;
  counterProps?: (device: TDevice) => {
    spaceId?: number;
    device?: TDevice;
    className?: string;
    inverted?: boolean;
    withTooltip?: boolean;
  };
  errors?: number;
  state?: string;
};

export type ProductType = {
  category: string;
  id: number;
  disabled?: boolean;
  owner: string;
  title: string;
  img: string;
  comingSoon?: boolean;
};

export enum ProductPricingModel {
  Standard = 'standard',
  UsageBased = 'usage_based',
  Personalized = 'personalized',
}

export type ModelType = {
  id: string;
};

export type MethodType = 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE';

export enum PeriodEnum {
  Monthly = 'monthly',
  Yearly = 'yearly',
  OneTime = 'one_time',
  UsageBased = 'usage_based',
}

export type UuidType = string;
export type AccessLevelType = 0 | 1 | 2 | 3;

export type ExpertType = {
  id: string;
  name: string;
  image: string;
  description: string;
  email?: string;
  phone?: string;
  locations?: string;
  expertise?: string;
};

export interface StateDumpType {
  id: string;
  filename: string;
  device_id: string;
  created_at: string;
  access_count: number;
  access_token: string;
  mime_type: string;
}

export type ValidationErrorType = {
  data: string;
  error: string;
  field: string;
  path: Array<string>;
};

export interface TestDeviceType {
  access_token: string;
  created_at: string;
  device_model_id: string;
  firmware: string;
  id: string;
  mac: null | string;
  cloud_id: string;
  sn: null | string;
  telemetry_count?: boolean;
  test: boolean;
  note: null | string;
  hub_url: null | string;
}

export interface TestDeviceWithTelemetryType extends TestDeviceType {
  telemetries: Array<TelemetryType>;
}

// Shipping

export enum ShipmentStatusType {
  Preparing = 'preparing_for_shipment',
  Shipped = 'shipped',
  Delivered = 'delivered',
}

// Shipping - Partners

export type PartnerInvoiceItemType = {
  name: string;
  amount: number;
};

export enum PaymentIntervalEnum {
  OneTime = 'one_time',
  Monthly = 'monthly',
  Yearly = 'yearly',
  UsageBased = 'usage_based',
}

export interface OrderPreviewItemType {
  id: string;
  name: string;
  quantity: number;
  selected_payment_interval: PaymentIntervalEnum;
  charges: {
    [PaymentIntervalEnum.OneTime]?: {
      amount_in_scu: number;
    };
    [PaymentIntervalEnum.Monthly]?: {
      charge_date: string;
      amount_in_scu: number;
    };
    [PaymentIntervalEnum.Yearly]?: {
      charge_date: string;
      amount_in_scu: number;
    };
  };
}

export interface EstimatedTax {
  name: string;
  rate: number;
  amount: number;
}

export interface PreviewOneTimeChargeSummary {
  type: 'one_time';
  amount_in_scu: number;
}

export interface PreviewProrataChargeSummary {
  type: 'prorata';
  amount_in_scu: number;
  start_date: string;
  end_date: string;
}

export interface MapCoordinatesType extends GoogleMapReact.Coords {}

export interface OrganizationAddressType {
  id: string;
  line_1: string;
  line_2: string | null;
  line_3: string | null;
  phone_number: string | null;
  city: string;
  state: string | null;
  country: string;
  zip: string | null;
  display_name: string;
  receiver_name: null;
  receiver_tax_id: null;
  eori: string | null;
  note_to_shipper: string | null;
}

export enum PaymentMethodEnum {
  CreditCard = 'credit_card',
  Lab = 'lab',
  ACHTransfer = 'ach_transfer',
  PurchaseOrder = 'purchase_order',
  Offline = 'offline',
}

const AUDIT_LOG_TEMPLATES = {
  user_created: 'user_created',
  user_added_to_group: 'user_added_to_group',
  user_removed_from_group: 'user_removed_from_group',
  changed_access_level: 'changed_access_level',
  removed_access: 'removed_access',
  group_created: 'group_created',
  update_group_permissions: 'update_group_permissions',
  device_claimed: 'device_claimed',
  device_deleted: 'device_deleted',
  device_command_sent: 'device_command_sent',
  integration_created: 'integration_created',
  integration_updated: 'integration_updated',
  customer_created: 'customer_created',
  model_created: 'model_created',
  model_published: 'model_published',
  product_created: 'product_created',
  product_updated: 'product_updated',
  product_deleted: 'product_deleted',
  invited_existing_partner: 'invited_existing_partner',
  invited_new_partner: 'invited_new_partner',
  partner_accepted_invitation: 'partner_accepted_invitation',
  partner_rejected_invitation: 'partner_rejected_invitation',
  attribute_updated: 'attribute_updated',
  purchase_order_approved: 'purchase_order_approved',
  purchase_order_uploaded: 'purchase_order_uploaded',
  device_license_attached: 'device_license_attached',
  incident_manually_closed: 'incident_manually_closed',
  offline_order_created: 'offline_order_created',
  license_code_generated: 'license_code_generated',
  redeemed_license_by_code: 'redeemed_license_by_code',
  manual_created: 'manual_created',
  manual_removed: 'manual_removed',
  section_created: 'section_created',
  section_deleted: 'section_deleted',
  section_edited: 'section_edited',
  section_duplicated: 'section_duplicated',
  widget_created: 'widget_created',
  widget_deleted: 'widget_deleted',
  widget_edited: 'widget_edited',
  widget_moved: 'widget_moved',
  connector_created: 'connector_created',
  connector_deleted: 'connector_deleted',
  connector_enabled: 'connector_enabled',
  connector_disabled: 'connector_disabled',
} as const;

export type AuditLogTemplates = keyof typeof AUDIT_LOG_TEMPLATES;

export interface AuditLogParamsType {
  id?: string;
  model?: string;
  name: string;
  type: string;
  value?: string | boolean;
  display_name: string;
}

export interface AuditLogType {
  id: string;
  full_message: string;
  created_at: string;
  user?: {
    id: string;
    name: string;
    super_admin: boolean;
  };
  template: AuditLogTemplates;
  params: Array<AuditLogParamsType>;
}

export interface TaxGroupProductType {
  id: string;
  name: string;
  image_url: string;
}

export interface UseNotes {
  note: string;
}

export interface UseCheckout {
  isSameAddress: {
    checked: StateType['store']['isSameAddress'];
    onChange: (isSameAddress: boolean) => void;
  };
  address: {
    billing: {
      selected: StateType['store']['billingAddressId'];
      onChange: (id: string | null) => void;
    };
    shipping: {
      selected: StateType['store']['shippingAddressId'];
      onChange: (id: string | null) => void;
    };
  };
  creditCard: {
    selected: StateType['store']['creditCardId'];
    onChange: (id: string | null) => void;
  };
  paymentMethod: {
    selected: StateType['store']['paymentMethod'];
    onChange: (paymentMethod: PaymentMethodEnum) => void;
  };
  note: {
    selected: StateType['store']['cart']['notes'];
    onChange: (notes: string) => void;
  };
}

export type DeviceModelIconName =
  | 'Mic'
  | 'Camera'
  | 'Cast'
  | 'Bell'
  | 'BarChart'
  | 'Disc'
  | 'Codepen'
  | 'Cpu'
  | 'Globe'
  | 'Box'
  | 'Briefcase'
  | 'Trash2'
  | 'Database'
  | 'Key'
  | 'Monitor'
  | 'Rss'
  | 'Users'
  | 'Phone'
  | 'Package'
  | 'Settings'
  | 'Pocket'
  | 'Video'
  | 'Volume'
  | 'Sliders'
  | 'Power'
  | 'Inbox'
  | 'Speaker'
  | 'Watch'
  | 'Voicemail'
  | 'Terminal'
  | 'Lock'
  | 'Grid'
  | 'Tablet'
  | 'Folder'
  | 'CreditCard'
  | 'Calendar'
  | 'Archive'
  | 'Aperture'
  | 'Coffee'
  | 'Eye'
  | 'HardDrive'
  | 'Layers'
  | 'Server'
  | 'Battery'
  | 'Target'
  | 'Zap'
  | 'LifeBuoy'
  | 'Printer';

export type OrderPaymentStatusType =
  | 'paid'
  | 'failed'
  | 'uncollectible'
  | 'pending'
  | 'refunded';

export type InvoiceStatusType = 'pending' | 'issued' | 'void';

export interface InvoiceItem {
  id: string;
  discount_in_scu: string;
  price_in_scu: number;
  prorated_price_in_scu: number;
  paid_period: { from: string; to: string };
  payment_interval: PaymentIntervalEnum;
  quantity: number;
  product_name: string;
  product_image_url: string;
  device_model_name: string;
  subscription_id: string;
  subscription_status: SubscriptionStatus;
  purchased_product_id: string;
  initial_order_short_id: string;
  order_id: string;
  payment_month_details: {
    days_in_purchased_month: number;
    days_left_in_purchased_month: number;
    days_paid_percentage_month: number;
  };
  usage_based: {
    usage_charge_in_scu: number;
    base_charge_in_scu: number;
    base_price_in_scu: number;
    usage_price_in_scu: number;
    usage_unit_name: string | null;
  } | null;
}

export type SubscriptionStatus =
  | 'active'
  | 'canceled'
  | 'suspended'
  | 'pending_cancellation';

export interface SubscriptionInvoiceItemType {
  id: string;
  created_at: string;
  paid_at: string | null;
  status: OrderPaymentStatusType;
  invoice_id: string | null;
  price_in_scu: number;
  start_period: string;
  end_period: string;
  usage_based: {
    meter_start: number | null;
    meter_end: number | null;
    usage_charge_in_scu: number;
    base_charge_in_scu: number;
  } | null;
}

export type PaymentMethodType =
  | 'lab'
  | 'credit_card'
  | 'purchase_order'
  | 'offline';

export type InvoiceTimelineEventTypeType =
  | 'invoice_generated'
  | 'credit_card_payment_failed'
  | 'purchase_order_uploaded'
  | 'purchase_order_approved'
  | 'credit_card_payment_received'
  | 'receipt_file_uploaded'
  | 'updated_credit_card';

export interface InvoiceTimelineEventType {
  id: string;
  title: string;
  description: string;
  created_at: string;
  type: InvoiceTimelineEventTypeType;
}

export enum ProductTypeEnum {
  Physical = 'physical',
  DeviceLicense = 'device_license',
  PlatformLicense = 'platform_license',
}

export interface CecPartnerType {
  id: string;
  name: string;
  display_name: string;
  logo: string | null;
  connected: boolean;
  about: string | null;
  organization_signup_enabled: boolean;
}
